import React, { useEffect, useRef } from 'react';
interface MatrixRainProps {
  className?: string;
  /** Color (rgb tuple string) */
  color?: string;
  /** Characters per column */
  density?: number;
  /** Font size in px */
  fontSize?: number;
}
/**
 * Matrix-style falling characters. Uses katakana + binary glyphs for an
 * AI / adversary feel. Pure canvas, no dependencies.
 */
export function MatrixRain({
  className = '',
  color = '26,143,212',
  density = 1,
  fontSize = 14
}: MatrixRainProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let raf = 0;
    const glyphs = 'アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン0123456789';
    const glyphArr = glyphs.split('');
    let drops: number[] = [];
    let columns = 0;
    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      const w = parent.offsetWidth;
      const h = parent.offsetHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      columns = Math.floor(w / fontSize * density);
      drops = Array.from({
        length: columns
      }).map(() => -Math.random() * (h / fontSize));
    };
    resize();
    window.addEventListener('resize', resize);
    const tick = () => {
      const w = canvas.width / dpr;
      const h = canvas.height / dpr;
      // Fade with dark layer for trailing effect
      ctx.fillStyle = 'rgba(5, 6, 8, 0.08)';
      ctx.fillRect(0, 0, w, h);
      ctx.font = `${fontSize}px monospace`;
      ctx.textBaseline = 'top';
      for (let i = 0; i < drops.length; i++) {
        const x = i / drops.length * w;
        const y = drops[i] * fontSize;
        const ch = glyphArr[Math.floor(Math.random() * glyphArr.length)];
        // Lead glyph (bright)
        ctx.fillStyle = `rgba(${color}, 0.95)`;
        ctx.shadowBlur = 8;
        ctx.shadowColor = `rgba(${color}, 0.8)`;
        ctx.fillText(ch, x, y);
        // Trail glyphs (dim)
        ctx.shadowBlur = 0;
        for (let t = 1; t < 6; t++) {
          const ty = y - t * fontSize;
          if (ty < 0) break;
          ctx.fillStyle = `rgba(${color}, ${0.4 - t * 0.06})`;
          const tch = glyphArr[Math.floor(Math.random() * glyphArr.length)];
          ctx.fillText(tch, x, ty);
        }
        if (y > h && Math.random() > 0.975) {
          drops[i] = -2;
        }
        drops[i] += 0.4;
      }
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(raf);
    };
  }, [color, density, fontSize]);
  return <canvas ref={canvasRef} className={`absolute inset-0 pointer-events-none ${className}`} data-id="element-133" />;
}